import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, BookOpen, CheckCircle, Edit2, Lightbulb, Plus, Save, Trash2, X } from 'lucide-react';
import type { ComiteNote, ComiteReunionComplete } from '../../services/comiteService';
import { addNote, updateNote, deleteNote } from '../../services/comiteService';

type ToastFn = (message: string, type: 'success' | 'error') => void;

interface NotesTabProps {
  reunion: ComiteReunionComplete;
  onUpdate: () => void;
  showToast: ToastFn;
}

export default function NotesTab({ reunion, onUpdate, showToast }: NotesTabProps) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editData, setEditData] = useState<Partial<ComiteNote>>({});
  const [newNote, setNewNote] = useState<Partial<ComiteNote>>({
    type: 'note_generale',
    contenu: '',
    auteur: '',
  });

  const grouped = useMemo(() => {
    const notes = reunion.notes || [];
    return {
      note_generale: notes.filter((n) => n.type === 'note_generale'),
      decision: notes.filter((n) => n.type === 'decision'),
      enjeu: notes.filter((n) => n.type === 'enjeu'),
      risque: notes.filter((n) => n.type === 'risque'),
    };
  }, [reunion.notes]);

  const handleAdd = async () => {
    try {
      if (!reunion.id) return;
      if (!newNote.contenu) return showToast('Le contenu est requis', 'error');
      await addNote({ ...newNote, reunion_id: reunion.id } as any);
      showToast('Note ajoutée!', 'success');
      setShowAddForm(false);
      setNewNote({ type: 'note_generale', contenu: '', auteur: '' });
      onUpdate();
    } catch (e) {
      console.error(e);
      showToast("Erreur lors de l'ajout", 'error');
    }
  };

  const startEdit = (note: ComiteNote) => {
    setEditingId(note.id || null);
    setEditData({ type: note.type, contenu: note.contenu, auteur: note.auteur });
  };

  const saveEdit = async () => {
    try {
      if (!editingId) return;
      if (!editData.contenu) return showToast('Le contenu est requis', 'error');
      await updateNote(editingId, editData);
      showToast('Note mise à jour!', 'success');
      setEditingId(null);
      setEditData({});
      onUpdate();
    } catch (e) {
      console.error(e);
      showToast('Erreur lors de la mise à jour', 'error');
    }
  };

  const remove = async (noteId: number) => {
    if (!confirm('Supprimer cette note?')) return;
    try {
      await deleteNote(noteId);
      showToast('Note supprimée!', 'success');
      onUpdate();
    } catch (e) {
      console.error(e);
      showToast('Erreur lors de la suppression', 'error');
    }
  };

  const sections: Array<{
    type: ComiteNote['type'];
    label: string;
    color: 'blue' | 'green' | 'orange' | 'red';
    icon: any;
  }> = [
    { type: 'note_generale', label: 'Notes', color: 'blue', icon: BookOpen },
    { type: 'decision', label: 'Décisions', color: 'green', icon: CheckCircle },
    { type: 'enjeu', label: 'Enjeux', color: 'orange', icon: AlertTriangle },
    { type: 'risque', label: 'Risques', color: 'red', icon: AlertTriangle },
  ];

  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
          <BookOpen className="w-7 h-7 text-blue-600" />
          Notes / Décisions / Enjeux
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowGuide(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Lightbulb className="w-4 h-4" />
            Guide
          </button>
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Ajouter
          </button>
        </div>
      </div>

      {showGuide && (
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4" onClick={() => setShowGuide(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden"
          >
            <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 text-white p-5 flex items-center justify-between">
              <div className="font-bold">Guide des bonnes pratiques</div>
              <button onClick={() => setShowGuide(false)} className="hover:bg-white/20 rounded-full p-2">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 text-sm text-gray-700 space-y-2">
              <p><strong>Notes:</strong> contexte + faits.</p>
              <p><strong>Décisions:</strong> QUI décide, QUOI, QUAND.</p>
              <p><strong>Enjeux/Risques:</strong> impact + prochaine action.</p>
            </div>
            <div className="p-4 bg-gray-50 border-t flex justify-end">
              <button onClick={() => setShowGuide(false)} className="px-5 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                Compris
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {showAddForm && (
        <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <select
              value={newNote.type}
              onChange={(e) => setNewNote({ ...newNote, type: e.target.value as any })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="note_generale">📝 Note</option>
              <option value="decision">✅ Décision</option>
              <option value="enjeu">⚠️ Enjeu</option>
              <option value="risque">🚨 Risque</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Contenu *</label>
            <textarea
              rows={3}
              value={newNote.contenu}
              onChange={(e) => setNewNote({ ...newNote, contenu: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Auteur</label>
            <input
              type="text"
              value={newNote.auteur || ''}
              onChange={(e) => setNewNote({ ...newNote, auteur: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center gap-3">
            <button onClick={handleAdd} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Ajouter
            </button>
            <button onClick={() => setShowAddForm(false)} className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
              Annuler
            </button>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {sections.map(({ type, label, color, icon: Icon }) => {
          const items = (grouped as any)[type] as ComiteNote[];
          if (!items.length) return null;
          return (
            <div key={type}>
              <h4 className={`font-bold text-${color}-700 mb-3 flex items-center gap-2`}>
                <Icon className="w-5 h-5" />
                {label} ({items.length})
              </h4>
              <div className="space-y-2">
                {items.map((note) => {
                  const isEditing = editingId === note.id;
                  return (
                    <div key={note.id} className={`bg-${color}-50 border-l-4 border-${color}-500 rounded-r-lg p-4 group`}>
                      {isEditing ? (
                        <div className="space-y-3">
                          <textarea
                            rows={3}
                            value={editData.contenu || ''}
                            onChange={(e) => setEditData({ ...editData, contenu: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                          <div className="flex items-center gap-2">
                            <button onClick={saveEdit} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                              <Save className="w-4 h-4" />
                              Sauvegarder
                            </button>
                            <button onClick={() => { setEditingId(null); setEditData({}); }} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
                              Annuler
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-gray-800 flex-1 whitespace-pre-wrap">{note.contenu}</p>
                            <div className="flex items-center gap-1">
                              <button onClick={() => startEdit(note)} className="opacity-0 group-hover:opacity-100 transition-opacity p-2 rounded-lg hover:bg-white/50" title="Modifier">
                                <Edit2 className="w-4 h-4 text-gray-700" />
                              </button>
                              {note.id && (
                                <button onClick={() => remove(note.id!)} className="opacity-0 group-hover:opacity-100 transition-opacity p-2 rounded-lg hover:bg-red-100" title="Supprimer">
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="mt-2 text-xs text-gray-500">
                            {note.auteur ? `— ${note.auteur}` : null}{' '}
                            {note.timestamp ? `• ${new Date(note.timestamp).toLocaleString('fr-CA')}` : null}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {(!reunion.notes || reunion.notes.length === 0) && (
          <div className="text-center py-12 bg-gray-50 rounded-xl">
            <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Aucune note pour le moment</p>
          </div>
        )}
      </div>
    </motion.div>
  );
}

